import {
  parseMultilineContent,
  stringifyMultilineContent,
  stringifyVariableAsPlaceholder,
} from "@repo/app-types/content/plaintext";
import type { MultilineContent } from "@repo/app-types/content/types";
import type { JobType } from "@repo/app-types/entities/jobType";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { Slideover } from "@repo/ui/Slideover/slideover";
import { type FC, useEffect, useEffectEvent, useState } from "react";
import { jobTypeApi } from "../../../../api/entities/jobType.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../../../hooks/hook.js";
import { TrainingMaterialsTextArea } from "./trainingMaterialsTextArea.js";

export interface TrainingMaterialsSlideoverProps {
  jobType: JobType;
  isOpen: boolean;
  onRequestClose: () => void;
}

export const TrainingMaterialsSlideover: FC<TrainingMaterialsSlideoverProps> = (
  props,
) => {
  const [content, setContent] = useState<MultilineContent>({ content: [] });
  const [stringifiedContent, setStringifiedContent] = useState<string>("");
  const [hasMadeChanges, setHasMadeChanges] = useState(false);

  // Reset state when the slideover opens.
  const handleOpen = useEffectEvent(() => {
    // TODO: Use freshly fetched job type to avoid initializing with stale state.
    setContent(props.jobType.job_description);
    setStringifiedContent(
      stringifyMultilineContent(props.jobType.job_description, {
        stringifyVariable: stringifyVariableAsPlaceholder,
      }),
    );
    setHasMadeChanges(false);
  });

  useEffect(() => {
    if (props.isOpen) {
      handleOpen();
    }
  }, [props.isOpen]);

  const dispatch = useAppDispatch();

  const handleTextareaChange = (newContent: string) => {
    setContent(
      parseMultilineContent(newContent, {
        parsesVariables: false,
      }),
    );
    setStringifiedContent(newContent);
    setHasMadeChanges(true);
  };

  // TODO: Use MultilineContentEditor.
  // const handleEditorChange = (newContent: MultilineContent) => {
  //   setContent(newContent);
  //   setHasMadeChanges(true);
  // };

  const handleSaveClick = () => {
    // TODO: Handle errors.
    dispatch(
      jobTypeApi.endpoints.updateJobType.initiate({
        id: props.jobType.id,
        job_description: content,
      }),
    );
    props.onRequestClose();
  };

  const { renderConfirmationModal, onConfirmThenClose: handleCloseClick } =
    useConfirmationModal({
      needsConfirmation: hasMadeChanges,
      onClose: props.onRequestClose,
    });

  const handleDiscardAndCloseClick = () => {
    props.onRequestClose();
  };

  return (
    <>
      <Slideover
        shouldShow={props.isOpen}
        onClose={handleCloseClick}
        title="Edit training materials"
        buttons={
          <>
            <Can
              I={PermissionActions.UPDATE}
              a={PermissionSubjects.JOB_OPENINGS}
              passThrough={true}
            >
              {(allowed) => (
                <Button
                  isDisabled={!allowed || !hasMadeChanges}
                  onClick={handleSaveClick}
                  size={Size.MEDIUM}
                  variant={ButtonVariant.Primary}
                >
                  Save changes
                </Button>
              )}
            </Can>
            <Button
              onClick={handleDiscardAndCloseClick}
              size={Size.MEDIUM}
              variant={ButtonVariant.Secondary}
            >
              Discard changes
            </Button>
          </>
        }
      >
        <div className="flex flex-auto flex-col justify-stretch px-3 py-2">
          <TrainingMaterialsTextArea
            value={stringifiedContent}
            onChange={handleTextareaChange}
            description={
              "Training materials that describe the content of the training session."
            }
            // This component doesn't want to grow to fit the space. :(
            height="400px"
          />

          {/* <MultilineContentEditor
          content={content}
          onChange={handleEditorChange}
        /> */}
        </div>
      </Slideover>

      {renderConfirmationModal()}
    </>
  );
};
